import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Typography, Chip, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { campaignService, campaignCategoryService, extractApiDataList } from '../services/api';
import { Add, Edit, Preview, Search, TrendingUp, TrendingDown, Flag, Category, Delete, Replay } from '@mui/icons-material';

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [actionId, setActionId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  useEffect(() => {
    campaignCategoryService
      .getAll({ include_inactive: 1 })
      .then((res) => setCategories(extractApiDataList(res)))
      .catch(() => setCategories([]));
  }, []);

  // Fetch campaigns whenever search, filter, sort, or page changes
  useEffect(() => {
    fetchCampaigns();
  }, [searchQuery, filterStatus, filterCategoryId, sortBy, pageNumber]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await campaignService.getAllCampaigns({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy,
        status: filterStatus,
        category_id: filterCategoryId || undefined,
      });
      
      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.success && response.data?.data) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      }
      
      // Enrich list rows with essentials metadata so admin can see configured lines at a glance.
      const enrichedData = await Promise.all(
        data.map(async (campaign) => {
          try {
            const essentialsRes = await campaignService.getCampaignEssentials(campaign.id);
            const essentialsPayload = essentialsRes.data?.data ?? essentialsRes.data ?? [];
            const essentials = Array.isArray(essentialsPayload) ? essentialsPayload : [];
            return {
              ...campaign,
              essentials_count: essentials.length,
              essentials_preview: essentials
                .map((e) => e?.item_name)
                .filter(Boolean)
                .slice(0, 2),
            };
          } catch (_) {
            return {
              ...campaign,
              essentials_count: 0,
              essentials_preview: [],
            };
          }
        })
      );

      setCampaigns(enrichedData);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const changeCampaignStatus = async (campaign, status) => {
    const isDelete = status === 'deleted';
    const confirmed = window.confirm(
      isDelete
        ? `Remove "${campaign.title}" from the website? It stays in the database and you can restart it later.`
        : `Restart "${campaign.title}"? It will show on the website again.`
    );
    if (!confirmed) return;
    try {
      setActionId(campaign.id);
      await campaignService.updateCampaignStatus(campaign.id, { status });
      setToast({
        open: true,
        message: isDelete ? 'Campaign removed from the website. Use the Deleted filter to restart it.' : 'Campaign restarted',
        type: 'success',
      });
      await fetchCampaigns();
    } catch (error) {
      setToast({
        open: true,
        message: error.response?.data?.error || 'Could not update the campaign',
        type: 'error',
      });
    } finally {
      setActionId(null);
    }
  };

  if (loading && campaigns.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Campaigns
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Create and manage fundraising campaigns
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Category />}
                onClick={() => navigate('/admin/campaign-categories')}
                sx={{ borderColor: '#2c5f2d', color: '#2c5f2d' }}
              >
                Categories
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Add />}
                onClick={() => navigate('/admin/campaigns/add')}
                sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
              >
                Add New Campaign
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Total Campaigns Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Campaigns
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  {pagination.totalItems}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Active campaigns
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Total Raised Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                <TrendingDown sx={{ color: '#4f46e5', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Raised
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                  ₹{campaigns.reduce((sum, c) => sum + (parseFloat(c.current_amount) || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Total collected
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Target Amount Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <Flag sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Target Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                  ₹{campaigns.reduce((sum, c) => sum + (parseFloat(c.target_amount) || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Total goal
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by title or description..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageNumber(1);
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategoryId}
                  label="Category"
                  onChange={(e) => {
                    setFilterCategoryId(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={String(cat.id)}>
                      {cat.name}{cat.is_active ? '' : ' (inactive)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="created_at-desc">Date (Newest)</MenuItem>
                  <MenuItem value="created_at-asc">Date (Oldest)</MenuItem>
                  <MenuItem value="title-asc">Title (A-Z)</MenuItem>
                  <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                  <MenuItem value="target_amount-asc">Target (Low to High)</MenuItem>
                  <MenuItem value="target_amount-desc">Target (High to Low)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ 
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Target Amount</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Raised</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Essentials</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns && campaigns.length > 0 ? (
                  campaigns.map(campaign => (
                    <TableRow 
                      key={campaign.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafb' },
                        borderBottom: '1px solid #e8ecf1'
                      }}
                    >
                      <TableCell sx={{ color: '#667085' }}>{campaign.id}</TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{campaign.title}</TableCell>
                      <TableCell>
                        {campaign.category?.name ? (
                          <Chip label={campaign.category.name} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#059669' }}>₹{campaign.target_amount.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#2c5f2d' }}>₹{campaign.current_amount.toLocaleString()}</TableCell>
                      <TableCell>
                        {campaign.essentials_count > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Chip
                              label={`${campaign.essentials_count} item${campaign.essentials_count > 1 ? 's' : ''}`}
                              size="small"
                              sx={{ width: 'fit-content', bgcolor: '#ecfdf3', color: '#166534', fontWeight: 600 }}
                            />
                            {campaign.essentials_preview?.length > 0 && (
                              <Typography variant="caption" sx={{ color: '#667085' }}>
                                {campaign.essentials_preview.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>None</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={campaign.status}
                          color={campaign.status === 'active' ? 'success' : campaign.status === 'deleted' ? 'error' : 'default'}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: '500' }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button 
                          size="small"
                          startIcon={<Preview />}
                          onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
                          sx={{ color: '#059669' }}
                        >
                          View
                        </Button>
                        <Button 
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                          sx={{ color: '#2c5f2d' }}
                        >
                          Edit
                        </Button>
                        {campaign.status === 'deleted' ? (
                          <Button
                            size="small"
                            startIcon={<Replay />}
                            disabled={actionId === campaign.id}
                            onClick={() => changeCampaignStatus(campaign, 'active')}
                            sx={{ color: '#059669' }}
                          >
                            Restart
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<Delete />}
                            disabled={actionId === campaign.id}
                            onClick={() => changeCampaignStatus(campaign, 'deleted')}
                            sx={{ color: '#b42318' }}
                          >
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#667085' }}>
                      No campaigns found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Footer */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Showing {campaigns.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} campaigns
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                size="small" 
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(pagination.totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent',
                    color: pageNumber === i + 1 ? '#fff' : '#667085',
                    borderColor: '#d5d9e2',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#059669' : '#f3f4f6',
                      borderColor: '#667085'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button 
                variant="outlined" 
                size="small"
                disabled={pageNumber === pagination.totalPages || pagination.totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Card>
      </Container>
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignsPage;
